import { AnchorProvider } from '@coral-xyz/anchor'
import { bs58 } from '@coral-xyz/anchor/dist/cjs/utils/bytes'
import { PublicKey } from '@solana/web3.js'
import {
  entityCreatorKey,
  keyToAssetKey,
  init as initHem,
} from '@helium/helium-entity-manager-sdk'
import { daoKey } from '@helium/helium-sub-daos-sdk'
import { HNT_MINT, searchAssets } from '@helium/spl-utils'
import { init } from '@helium/lazy-distributor-sdk'
import { getPendingRewards } from '@helium/distributor-oracle'
import {
  Mints,
  MOBILE_LAZY_KEY,
  IOT_LAZY_KEY,
  HNT_LAZY_KEY,
} from '@utils/constants'
import { CompressedNFT } from 'types/solana'

/**
 * Everything the treasury needs to know about one hotspot NFT. Flat on
 * purpose: this is what crosses the wire to the browser.
 */
export type HotspotSummary = {
  id: string
  name: string
  image: string
  pendingRewards: Record<string, string>
}

export const REWARD_MINTS = [Mints.MOBILE, Mints.IOT, Mints.HNT]

export const zeroRewards = (): Record<string, string> =>
  REWARD_MINTS.reduce(
    (acc, mint) => ({ ...acc, [mint]: '0' }),
    {} as Record<string, string>,
  )

export const HOTSPOT_PAGE_SIZE = 1000

export const heliumEntityCreator = () =>
  entityCreatorKey(daoKey(HNT_MINT)[0])[0]

/**
 * Every Helium hotspot NFT owned by `owner`, paged out of the DAS index until
 * a short page comes back.
 */
export const searchHotspotAssets = async (
  rpcEndpoint: string,
  owner: string,
  pageSize = HOTSPOT_PAGE_SIZE,
): Promise<CompressedNFT[]> => {
  const creatorAddress = heliumEntityCreator().toBase58()
  const all: CompressedNFT[] = []

  for (let page = 1; ; page += 1) {
    const items = (await searchAssets(rpcEndpoint, {
      ownerAddress: owner,
      creatorVerified: true,
      creatorAddress,
      page,
      limit: pageSize,
      options: { showCollectionMetadata: true },
    } as any)) as unknown as CompressedNFT[]

    if (!items?.length) break
    all.push(...items)
    if (items.length < pageSize) break
  }

  return all
}

/** The keyToAsset account address for a hotspot, however it is encoded. */
const keyToAssetAddress = (hotspot: CompressedNFT): string | undefined => {
  try {
    if (hotspot.creators?.length > 1) {
      const address = hotspot.creators[1]?.address
      return typeof address === 'string' ? address : address?.toBase58()
    }

    return keyToAssetKey(
      daoKey(HNT_MINT)[0],
      hotspot.content.json_uri.split('/').slice(-1)[0],
      'b58',
    )[0].toBase58()
  } catch (e) {
    console.error('Failed to derive keyToAsset address', e)
    return undefined
  }
}

/**
 * Pending MOBILE / IOT / HNT rewards for each hotspot, aligned by index with
 * the hotspots passed in. Three oracle calls total, not three per hotspot.
 */
export const getPendingRewardsForHotspots = async (
  provider: AnchorProvider,
  hotspots: CompressedNFT[],
): Promise<Record<string, string>[]> => {
  if (!hotspots.length) return []

  const program = await init(provider)
  const hemProgram = await initHem(provider)
  const dao = daoKey(new PublicKey(Mints.HNT))[0]

  const ktaAddresses = hotspots.map(keyToAssetAddress)
  const ktaAccounts = await hemProgram.account.keyToAssetV0.fetchMultiple(
    ktaAddresses.map((address) =>
      address ? new PublicKey(address) : PublicKey.default,
    ),
  )

  const entityKeys = hotspots.map((_, index) => {
    const account = ktaAddresses[index] ? ktaAccounts[index] : undefined
    if (!account?.entityKey) return undefined
    return bs58.encode(account.entityKey as Buffer)
  })

  const known = entityKeys.filter((key): key is string => !!key)
  if (!known.length) return hotspots.map(() => zeroRewards())

  const [mobileRewards, iotRewards, hntRewards] = await Promise.all([
    getPendingRewards(program, MOBILE_LAZY_KEY, dao, known),
    getPendingRewards(program, IOT_LAZY_KEY, dao, known),
    getPendingRewards(program, HNT_LAZY_KEY, dao, known),
  ])

  return entityKeys.map((entityKey) => {
    if (!entityKey) return zeroRewards()
    return {
      [Mints.MOBILE]: mobileRewards[entityKey] || '0',
      [Mints.IOT]: iotRewards[entityKey] || '0',
      [Mints.HNT]: hntRewards[entityKey] || '0',
    }
  })
}

export const toHotspotSummary = (
  hotspot: CompressedNFT,
  pendingRewards: Record<string, string>,
): HotspotSummary => ({
  id:
    typeof hotspot.id === 'string'
      ? hotspot.id
      : (hotspot.id as unknown as PublicKey)?.toBase58(),
  name: hotspot.content?.metadata?.name || '',
  image: hotspot.content?.links?.image || '',
  pendingRewards,
})
